import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ManhwaCard from "@/components/feature/ManhwaCard";
import {
  heroManhwas,
  latestManhwas,
  popularManhwas,
  topRatedManhwas,
  categories,
} from "@/mocks/manhwa";

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroManhwas.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const currentHero = heroManhwas[heroIndex];

  const homepageSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "韩漫大全",
    url: "https://www.manhwadaquan.com/",
    description:
      "韩漫漫画网汇聚众多韩漫大全作品，推荐好看的韩国漫画免费在线观看，加载速度快画质清晰，是您探索韩漫世界的理想平台。",
    potentialAction: {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.manhwadaquan.com/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[580px] overflow-hidden">
          <div className="absolute inset-0 bg-background-900">
            <img
              src={currentHero.cover}
              alt={currentHero.title}
              className="w-full h-full object-cover opacity-60 transition-opacity duration-700"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />

          <div className="relative z-10 w-full h-full max-w-screen-2xl mx-auto px-4 md:px-6 flex items-center">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 w-full">
              <div className="w-36 md:w-48 flex-shrink-0">
                <Link
                  to={`/manhwa/${currentHero.slug}`}
                  className="block rounded-xl overflow-hidden shadow-2xl transition-transform duration-300 hover:scale-105"
                >
                  <img
                    src={currentHero.cover}
                    alt={currentHero.title}
                    className="w-full aspect-[3/4] object-cover"
                  />
                </Link>
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white">
                    {currentHero.categoryName}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      currentHero.status === "已完结"
                        ? "bg-secondary-500 text-white"
                        : "bg-accent-500 text-white"
                    }`}
                  >
                    {currentHero.status}
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white font-serif leading-tight">
                  <Link
                    to={`/manhwa/${currentHero.slug}`}
                    className="hover:text-primary-300 transition-colors"
                  >
                    {currentHero.title}
                  </Link>
                </h1>
                <p className="mt-3 text-sm md:text-base text-white/80 max-w-lg mx-auto md:mx-0 line-clamp-2">
                  {currentHero.description}
                </p>
                <div className="mt-4 flex items-center justify-center md:justify-start gap-6 text-sm text-white/70">
                  <span>
                    <i className="ri-user-line mr-1" />
                    {currentHero.author}
                  </span>
                  <span>
                    <i className="ri-star-fill text-amber-400 mr-1" />
                    {currentHero.rating}
                  </span>
                  <span>
                    <i className="ri-file-list-3-line mr-1" />
                    {currentHero.chapters}话
                  </span>
                </div>
                <div className="mt-6 flex items-center justify-center md:justify-start gap-3">
                  <Link
                    to={`/manhwa/${currentHero.slug}`}
                    className="inline-flex items-center px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap text-sm"
                  >
                    <i className="ri-play-circle-line mr-2 text-lg" />
                    开始阅读
                  </Link>
                  <Link
                    to={`/category/${currentHero.category}`}
                    className="inline-flex items-center px-5 py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm backdrop-blur-sm"
                  >
                    浏览更多
                    <i className="ri-arrow-right-line ml-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Hero dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {heroManhwas.map((_, index) => (
              <button
                key={index}
                onClick={() => setHeroIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === heroIndex
                    ? "bg-primary-500 w-6"
                    : "bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="relative z-20 -mt-8">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 py-5 px-4 shadow-sm">
              <div className="flex items-center flex-wrap justify-center gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-all duration-200 whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-sm`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-serif">
                  推荐佳作
                </h2>
                <p className="mt-1 text-sm text-foreground-500">
                  精选热门韩漫，不容错过
                </p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {heroManhwas.map((manhwa) => (
                <ManhwaCard key={manhwa.id} {...manhwa} />
              ))}
            </div>
          </div>
        </section>

        {/* Popular Ranking + Latest Updates */}
        <section className="py-12 md:py-16 bg-background-100/70">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Popular Ranking */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-serif">
                      人气排行
                    </h2>
                    <p className="mt-1 text-sm text-foreground-500">
                      最受欢迎的韩漫作品
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {popularManhwas.slice(0, 8).map((manhwa, index) => (
                    <Link
                      key={manhwa.id}
                      to={`/manhwa/${manhwa.slug}`}
                      className="flex items-center gap-4 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 group"
                    >
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold flex-shrink-0 ${
                          index < 3
                            ? "bg-primary-500 text-white"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={manhwa.cover}
                          alt={manhwa.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {manhwa.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {manhwa.author}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-foreground-500 flex-shrink-0">
                        <i className="ri-fire-line text-primary-500" />
                        {formatViews(manhwa.views)}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Latest Updates */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-serif">
                      最近更新
                    </h2>
                    <p className="mt-1 text-sm text-foreground-500">
                      最新连载的韩漫章节
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {latestManhwas.slice(0, 8).map((manhwa) => (
                    <Link
                      key={manhwa.id}
                      to={`/manhwa/${manhwa.slug}`}
                      className="flex items-center gap-4 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 group"
                    >
                      <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={manhwa.cover}
                          alt={manhwa.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {manhwa.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {manhwa.categoryName} · {manhwa.chapters}话
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-xs text-foreground-500">
                          {formatDate(manhwa.updatedAt)}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                          更新
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Top Rated Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-serif">
                  高分推荐
                </h2>
                <p className="mt-1 text-sm text-foreground-500">
                  评分最高的精选韩漫
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {topRatedManhwas.slice(0, 6).map((manhwa) => (
                <ManhwaCard key={manhwa.id} {...manhwa} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-background-900">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20comic%20art%20background%2C%20beautiful%20illustration%20with%20warm%20golden%20lighting%2C%20dreamy%20atmospheric%20scene%20with%20floating%20sparkles%2C%20soft%20romantic%20mood%2C%20elegant%20anime%20art%20style%2C%20magical%20glowing%20elements&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background-900/60 to-background-900/80" />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white font-serif">
              开启您的韩漫阅读之旅
            </h2>
            <p className="mt-3 text-sm md:text-base text-white/70 max-w-lg mx-auto">
              海量韩漫免费在线观看，每日更新，高清画质，随时随地畅享精彩故事
            </p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/all"
                className="inline-flex items-center px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap text-sm"
              >
                <i className="ri-book-open-line mr-2 text-lg" />
                浏览全部漫画
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center px-5 py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-all duration-200 whitespace-nowrap text-sm backdrop-blur-sm"
              >
                了解更多
                <i className="ri-arrow-right-line ml-1" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function formatViews(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + "万";
  }
  return num.toString();
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return diffHours + "小时前";
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return diffDays + "天前";
  return dateStr.slice(5);
}